// 玩创模式存储服务 - 管理聊天记录、角色状态、日记等
import enhancedStorageService from './enhancedStorageService.js'
import { v4 as uuidv4 } from 'uuid'

class PlayCreateStorageService {
  constructor() {
    this.db = null
  }

  /**
   * 确保数据库已初始化
   */
  async ensureDB() {
    if (!this.db) {
      this.db = await enhancedStorageService.ensureDB()
    }
    return this.db
  }

  // ==================== 角色档案管理 ====================

  /**
   * 保存角色档案
   */
  async saveCharacterProfile(character) {
    try {
      console.log('💾 [saveCharacterProfile] 接收到的 character 参数:', {
        id: character.id,
        name: character.name,
        hasAvatar: !!character.avatar,
        hasBackground: !!character.background,
        avatarLength: character.avatar?.length,
        backgroundLength: character.background?.length,
        avatarUrl: character.avatar,
        backgroundUrl: character.background,
        enableAdultMode: character.enableAdultMode
      })
      
      await this.ensureDB()
      const transaction = this.db.transaction(['characterProfiles'], 'readwrite')
      const store = transaction.objectStore('characterProfiles')

      const characterData = {
        id: character.id || uuidv4(),
        name: character.name,
        avatar: character.avatar || '', // 当前显示的立绘
        background: character.background || '', // 当前显示的背景
        originalAvatar: character.originalAvatar || character.avatar || '', // 保存原始立绘
        originalBackground: character.originalBackground || character.background || '', // 保存原始背景
        description: character.description,
        personality: character.personality,
        systemPrompt: character.systemPrompt || '',
        optimizedSystemPrompt: character.optimizedSystemPrompt || '', // 保存优化后的提示词
        optimizedPersonality: character.optimizedPersonality || null, // 保存优化后的人设
        enableAdultMode: character.enableAdultMode || false, // NSFW模式开关
        tags: character.tags || [], // 标签
        status: character.status || 'draft', // 状态：draft, published, trash
        isPublic: character.isPublic || false, // 是否已发布
        createdAt: character.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      
      console.log('💾 [saveCharacterProfile] 准备写入数据库的 characterData:', {
        id: characterData.id,
        hasAvatar: !!characterData.avatar,
        hasBackground: !!characterData.background,
        avatarLength: characterData.avatar?.length,
        backgroundLength: characterData.background?.length,
        avatarUrl: characterData.avatar,
        backgroundUrl: characterData.background,
        enableAdultMode: characterData.enableAdultMode
      })

      return new Promise((resolve, reject) => {
        const request = store.put(characterData)
        request.onsuccess = () => {
          console.log('✅ [saveCharacterProfile] 成功写入IndexedDB:', {
            id: characterData.id,
            name: characterData.name,
            hasAvatar: !!characterData.avatar,
            hasBackground: !!characterData.background,
            avatarUrl: characterData.avatar,
            backgroundUrl: characterData.background,
            enableAdultMode: characterData.enableAdultMode
          })
          resolve(characterData)
        }
        request.onerror = () => {
          console.error('❌ [saveCharacterProfile] 写入失败:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('❌ [saveCharacterProfile] 异常:', error)
      throw error
    }
  }

  /**
   * 获取角色档案
   */
  async getCharacterProfile(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterProfiles'], 'readonly')
      const store = transaction.objectStore('characterProfiles')

      return new Promise((resolve, reject) => {
        const request = store.get(characterId)
        request.onsuccess = () => {
          const result = request.result || null
          console.log('📖 [getCharacterProfile] 从IndexedDB读取:', {
            id: characterId,
            found: !!result,
            name: result?.name,
            hasAvatar: !!result?.avatar,
            hasBackground: !!result?.background,
            avatarLength: result?.avatar?.length,
            backgroundLength: result?.background?.length,
            enableAdultMode: result?.enableAdultMode
          })
          resolve(result)
        }
        request.onerror = () => {
          console.error('❌ [getCharacterProfile] 读取失败:', request.error)
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('❌ 获取角色档案失败:', error)
      return null
    }
  }

  /**
   * 获取所有角色档案
   */
  async getAllCharacterProfiles() {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterProfiles'], 'readonly')
      const store = transaction.objectStore('characterProfiles')

      return new Promise((resolve, reject) => {
        const request = store.getAll()
        request.onsuccess = () => {
          const profiles = request.result || []
          console.log(`✅ 加载了 ${profiles.length} 个角色档案`)
          resolve(profiles)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取所有角色档案失败:', error)
      return []
    }
  }

  /**
   * 删除角色档案
   */
  async deleteCharacterProfile(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterProfiles'], 'readwrite')
      const store = transaction.objectStore('characterProfiles')

      return new Promise((resolve, reject) => {
        const request = store.delete(characterId)
        request.onsuccess = () => {
          console.log('🗑️ 角色档案已删除:', characterId)
          resolve()
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 删除角色档案失败:', error)
      throw error
    }
  }

  /**
   * 完整删除角色及其所有相关数据
   * 包括：档案、聊天会话、状态、日记、服装、表情、摘要、记忆
   */
  async deleteCharacterCompletely(characterId) {
    try {
      console.log('🗑️ 开始完整删除角色及相关数据:', characterId)
      
      await this.ensureDB()
      
      const storeNames = [
        'characterProfiles',
        'chatSessions',
        'characterStates',
        'characterDiaries',
        'characterClothing',
        'characterExpressions',
        'chatSummaries',
        'characterMemories'
      ]
      
      // 确保所有存储都存在
      const availableStores = storeNames.filter(name => 
        this.db.objectStoreNames.contains(name)
      )
      
      // 创建事务删除所有相关数据
      const transaction = this.db.transaction(availableStores, 'readwrite')
      const deletePromises = []
      
      // 1. 删除角色档案
      if (availableStores.includes('characterProfiles')) {
        const profileStore = transaction.objectStore('characterProfiles')
        deletePromises.push(new Promise((resolve, reject) => {
          const request = profileStore.delete(characterId)
          request.onsuccess = () => {
            console.log('  ✓ 角色档案已删除')
            resolve()
          }
          request.onerror = () => reject(request.error)
        }))
      }
      
      // 2. 删除角色状态
      if (availableStores.includes('characterStates')) {
        const stateStore = transaction.objectStore('characterStates')
        deletePromises.push(new Promise((resolve, reject) => {
          const request = stateStore.delete(characterId)
          request.onsuccess = () => {
            console.log('  ✓ 角色状态已删除')
            resolve()
          }
          request.onerror = () => reject(request.error)
        }))
      }
      
      // 3. 删除聊天会话（通过索引查找并删除）
      if (availableStores.includes('chatSessions')) {
        const sessionStore = transaction.objectStore('chatSessions')
        const sessionIndex = sessionStore.index('characterId')
        deletePromises.push(new Promise((resolve, reject) => {
          const request = sessionIndex.getAllKeys(characterId)
          request.onsuccess = () => {
            const keys = request.result || []
            const deleteRequests = keys.map(key => 
              new Promise((res, rej) => {
                const delReq = sessionStore.delete(key)
                delReq.onsuccess = () => res()
                delReq.onerror = () => rej(delReq.error)
              })
            )
            Promise.all(deleteRequests).then(() => {
              console.log(`  ✓ ${keys.length} 个聊天会话已删除`)
              resolve()
            }).catch(reject)
          }
          request.onerror = () => reject(request.error)
        }))
      }
      
      // 4. 删除角色日记
      if (availableStores.includes('characterDiaries')) {
        const diaryStore = transaction.objectStore('characterDiaries')
        const diaryIndex = diaryStore.index('characterId')
        deletePromises.push(new Promise((resolve, reject) => {
          const request = diaryIndex.getAllKeys(characterId)
          request.onsuccess = () => {
            const keys = request.result || []
            const deleteRequests = keys.map(key => 
              new Promise((res, rej) => {
                const delReq = diaryStore.delete(key)
                delReq.onsuccess = () => res()
                delReq.onerror = () => rej(delReq.error)
              })
            )
            Promise.all(deleteRequests).then(() => {
              console.log(`  ✓ ${keys.length} 个角色日记已删除`)
              resolve()
            }).catch(reject)
          }
          request.onerror = () => reject(request.error)
        }))
      }
      
      // 5. 删除角色服装
      if (availableStores.includes('characterClothing')) {
        const clothingStore = transaction.objectStore('characterClothing')
        const clothingIndex = clothingStore.index('characterId')
        deletePromises.push(new Promise((resolve, reject) => {
          const request = clothingIndex.getAllKeys(characterId)
          request.onsuccess = () => {
            const keys = request.result || []
            const deleteRequests = keys.map(key => 
              new Promise((res, rej) => {
                const delReq = clothingStore.delete(key)
                delReq.onsuccess = () => res()
                delReq.onerror = () => rej(delReq.error)
              })
            )
            Promise.all(deleteRequests).then(() => {
              console.log(`  ✓ ${keys.length} 个角色服装已删除`)
              resolve()
            }).catch(reject)
          }
          request.onerror = () => reject(request.error)
        }))
      }
      
      // 6. 删除角色表情
      if (availableStores.includes('characterExpressions')) {
        const expressionStore = transaction.objectStore('characterExpressions')
        const expressionIndex = expressionStore.index('characterId')
        deletePromises.push(new Promise((resolve, reject) => {
          const request = expressionIndex.getAllKeys(characterId)
          request.onsuccess = () => {
            const keys = request.result || []
            const deleteRequests = keys.map(key => 
              new Promise((res, rej) => {
                const delReq = expressionStore.delete(key)
                delReq.onsuccess = () => res()
                delReq.onerror = () => rej(delReq.error)
              })
            )
            Promise.all(deleteRequests).then(() => {
              console.log(`  ✓ ${keys.length} 个角色表情已删除`)
              resolve()
            }).catch(reject)
          }
          request.onerror = () => reject(request.error)
        }))
      }
      
      // 7. 删除聊天摘要
      if (availableStores.includes('chatSummaries')) {
        const summaryStore = transaction.objectStore('chatSummaries')
        const summaryIndex = summaryStore.index('characterId')
        deletePromises.push(new Promise((resolve, reject) => {
          const request = summaryIndex.getAllKeys(characterId)
          request.onsuccess = () => {
            const keys = request.result || []
            const deleteRequests = keys.map(key => 
              new Promise((res, rej) => {
                const delReq = summaryStore.delete(key)
                delReq.onsuccess = () => res()
                delReq.onerror = () => rej(delReq.error)
              })
            )
            Promise.all(deleteRequests).then(() => {
              console.log(`  ✓ ${keys.length} 个聊天摘要已删除`)
              resolve()
            }).catch(reject)
          }
          request.onerror = () => reject(request.error)
        }))
      }
      
      // 8. 删除角色记忆
      if (availableStores.includes('characterMemories')) {
        const memoryStore = transaction.objectStore('characterMemories')
        const memoryIndex = memoryStore.index('characterId')
        deletePromises.push(new Promise((resolve, reject) => {
          const request = memoryIndex.getAllKeys(characterId)
          request.onsuccess = () => {
            const keys = request.result || []
            const deleteRequests = keys.map(key => 
              new Promise((res, rej) => {
                const delReq = memoryStore.delete(key)
                delReq.onsuccess = () => res()
                delReq.onerror = () => rej(delReq.error)
              })
            )
            Promise.all(deleteRequests).then(() => {
              console.log(`  ✓ ${keys.length} 个角色记忆已删除`)
              resolve()
            }).catch(reject)
          }
          request.onerror = () => reject(request.error)
        }))
      }
      
      // 等待所有删除操作完成
      await Promise.all(deletePromises)
      
      console.log('✅ 角色及所有相关数据已完整删除:', characterId)
      return true
    } catch (error) {
      console.error('❌ 完整删除角色失败:', error)
      throw error
    }
  }

  // ==================== 聊天会话管理 ====================

  /**
   * 保存聊天会话
   */
  async saveChatSession(characterId, messages) {
    try {
      // 先获取现有会话（使用独立的只读事务）
      const existingSession = await this.getChatSession(characterId)
      const sessionId = existingSession?.id || uuidv4()

      // 然后创建新的读写事务进行保存
      await this.ensureDB()
      const transaction = this.db.transaction(['chatSessions'], 'readwrite')
      const store = transaction.objectStore('chatSessions')

      const sessionData = {
        id: sessionId,
        characterId,
        messages: messages || [],
        createdAt: existingSession?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        metadata: {
          totalMessages: messages?.length || 0,
          totalImages: messages?.filter(m => m.type === 'image').length || 0,
          lastActiveTime: Date.now()
        }
      }

      return new Promise((resolve, reject) => {
        const request = store.put(sessionData)
        request.onsuccess = () => {
          console.log('✅ 聊天会话已保存:', sessionId)
          resolve(sessionData)
        }
        request.onerror = () => reject(request.error)
        transaction.onerror = () => reject(transaction.error)
      })
    } catch (error) {
      console.error('❌ 保存聊天会话失败:', error)
      throw error
    }
  }

  /**
   * 获取聊天会话
   */
  async getChatSession(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['chatSessions'], 'readonly')
      const store = transaction.objectStore('chatSessions')
      const index = store.index('characterId')

      return new Promise((resolve, reject) => {
        const request = index.get(characterId)
        request.onsuccess = () => {
          const result = request.result
          // 如果有多个会话，返回最新的
          if (result) {
            resolve(result)
          } else {
            // 如果没有找到，返回null
            index.getAll(characterId).onsuccess = (e) => {
              const all = e.target.result || []
              if (all.length > 0) {
                // 按更新时间排序，返回最新的
                all.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                resolve(all[0])
              } else {
                resolve(null)
              }
            }
          }
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取聊天会话失败:', error)
      return null
    }
  }

  /**
   * 添加消息到会话
   */
  async addMessageToSession(characterId, message) {
    try {
      const session = await this.getChatSession(characterId)
      const messages = session?.messages || []
      const newMessage = {
        id: uuidv4(),
        ...message,
        timestamp: message.timestamp || Date.now()
      }
      messages.push(newMessage)
      await this.saveChatSession(characterId, messages)
      return newMessage
    } catch (error) {
      console.error('❌ 添加消息失败:', error)
      throw error
    }
  }

  // ==================== 角色状态管理 ====================

  /**
   * 保存或更新角色状态
   */
  async saveCharacterState(characterId, state) {
    try {
      // 先获取现有状态（使用独立的只读事务）
      const existingState = await this.getCharacterState(characterId)
      
      // 然后创建新的读写事务进行保存
      await this.ensureDB()
      const transaction = this.db.transaction(['characterStates'], 'readwrite')
      const store = transaction.objectStore('characterStates')

      const stateData = {
        characterId,
        favorability: state.favorability ?? existingState?.favorability ?? 0,
        firstChatTime: state.firstChatTime || existingState?.firstChatTime || Date.now(),
        lastChatTime: state.lastChatTime || Date.now(),
        totalChatCount: state.totalChatCount ?? (existingState?.totalChatCount || 0) + 1,
        currentLevel: state.currentLevel || this.calculateFavorabilityLevel(state.favorability ?? existingState?.favorability ?? 0),
        currentClothingId: state.currentClothingId !== undefined ? state.currentClothingId : (existingState?.currentClothingId || null),
        // 点数系统
        points: state.points !== undefined ? state.points : (existingState?.points !== undefined ? existingState.points : 1000), // 初始1000点
        pointsHistory: state.pointsHistory || existingState?.pointsHistory || [],
        updatedAt: new Date().toISOString()
      }

      return new Promise((resolve, reject) => {
        const request = store.put(stateData)
        request.onsuccess = () => {
          console.log('✅ 角色状态已保存:', characterId)
          resolve(stateData)
        }
        request.onerror = () => reject(request.error)
        transaction.onerror = () => reject(transaction.error)
      })
    } catch (error) {
      console.error('❌ 保存角色状态失败:', error)
      throw error
    }
  }

  /**
   * 获取角色状态
   */
  async getCharacterState(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterStates'], 'readonly')
      const store = transaction.objectStore('characterStates')

      return new Promise((resolve, reject) => {
        const request = store.get(characterId)
        request.onsuccess = () => {
          const result = request.result
          if (!result) {
            // 如果没有状态，创建默认状态
            const defaultState = {
              characterId,
              favorability: 0,
              firstChatTime: Date.now(),
              lastChatTime: Date.now(),
              totalChatCount: 0,
              currentLevel: '陌生',
              updatedAt: new Date().toISOString()
            }
            resolve(defaultState)
          } else {
            resolve(result)
          }
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取角色状态失败:', error)
      return null
    }
  }

  /**
   * 更新好感度
   */
  async updateFavorability(characterId, delta) {
    try {
      const state = await this.getCharacterState(characterId)
      const newFavorability = Math.max(0, Math.min(100, (state.favorability || 0) + delta))
      await this.saveCharacterState(characterId, {
        ...state,
        favorability: newFavorability,
        currentLevel: this.calculateFavorabilityLevel(newFavorability)
      })
      return newFavorability
    } catch (error) {
      console.error('❌ 更新好感度失败:', error)
      throw error
    }
  }

  /**
   * 计算好感度等级
   */
  calculateFavorabilityLevel(favorability) {
    if (favorability < 20) return '陌生'
    if (favorability < 50) return '熟悉'
    if (favorability < 80) return '友好'
    return '亲密'
  }

  /**
   * 计算相处天数
   */
  calculateDaysTogether(firstChatTime) {
    if (!firstChatTime) return 0
    const now = Date.now()
    const diff = now - firstChatTime
    return Math.floor(diff / (1000 * 60 * 60 * 24))
  }

  // ==================== 角色日记管理 ====================

  /**
   * 保存角色日记
   */
  async saveDiary(characterId, diary) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterDiaries'], 'readwrite')
      const store = transaction.objectStore('characterDiaries')

      const diaryData = {
        id: diary.id || uuidv4(),
        characterId,
        date: diary.date || Date.now(),
        content: diary.content,
        favorabilityRequirement: diary.favorabilityRequirement || 0,
        unlocked: diary.unlocked !== undefined ? diary.unlocked : false,
        relatedChatIds: diary.relatedChatIds || [],
        createdAt: new Date().toISOString()
      }

      return new Promise((resolve, reject) => {
        const request = store.put(diaryData)
        request.onsuccess = () => {
          console.log('✅ 角色日记已保存:', diaryData.id)
          resolve(diaryData)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 保存角色日记失败:', error)
      throw error
    }
  }

  /**
   * 获取角色的所有日记
   */
  async getCharacterDiaries(characterId, favorability = 0) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterDiaries'], 'readonly')
      const store = transaction.objectStore('characterDiaries')
      const index = store.index('characterId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(characterId)
        request.onsuccess = () => {
          const diaries = (request.result || []).map(diary => ({
            ...diary,
            unlocked: diary.favorabilityRequirement <= favorability || diary.unlocked
          }))
          // 按日期排序
          diaries.sort((a, b) => b.date - a.date)
          resolve(diaries)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取角色日记失败:', error)
      return []
    }
  }

  // ==================== 礼物管理 ====================

  /**
   * 初始化礼物目录（预设礼物）
   */
  async initializeGiftCatalog() {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['giftCatalog'], 'readwrite')
      const store = transaction.objectStore('giftCatalog')

      // 检查是否已初始化
      const countRequest = store.count()
      const count = await new Promise((resolve) => {
        countRequest.onsuccess = () => resolve(countRequest.result)
        countRequest.onerror = () => resolve(0)
      })

      if (count > 0) {
        console.log('✅ 礼物目录已存在')
        return
      }

      // 预设礼物
      const defaultGifts = [
        { id: 'gift-1', name: '玫瑰花', description: '表达爱意的经典礼物', price: 50, rarity: 'common', effect: { favorabilityBonus: 5, unlockDiary: false }, imageUrl: '' },
        { id: 'gift-2', name: '巧克力', description: '甜蜜的礼物', price: 30, rarity: 'common', effect: { favorabilityBonus: 3, unlockDiary: false }, imageUrl: '' },
        { id: 'gift-3', name: '手工贺卡', description: '用心制作的贺卡', price: 20, rarity: 'common', effect: { favorabilityBonus: 2, unlockDiary: false }, imageUrl: '' },
        { id: 'gift-4', name: '精美饰品', description: '精致的饰品', price: 100, rarity: 'rare', effect: { favorabilityBonus: 10, unlockDiary: false }, imageUrl: '' },
        { id: 'gift-5', name: '书籍', description: '知识的礼物', price: 40, rarity: 'common', effect: { favorabilityBonus: 4, unlockDiary: false }, imageUrl: '' },
        { id: 'gift-6', name: '音乐盒', description: '美妙的音乐', price: 80, rarity: 'rare', effect: { favorabilityBonus: 8, unlockDiary: false }, imageUrl: '' },
        { id: 'gift-7', name: '手写信', description: '真挚的文字', price: 10, rarity: 'common', effect: { favorabilityBonus: 1, unlockDiary: false }, imageUrl: '' },
        { id: 'gift-8', name: '定制礼物', description: '特别的定制礼物', price: 200, rarity: 'epic', effect: { favorabilityBonus: 20, unlockDiary: true }, imageUrl: '' },
        { id: 'gift-9', name: '香水', description: '优雅的香气', price: 150, rarity: 'rare', effect: { favorabilityBonus: 12, unlockDiary: false }, imageUrl: '' },
        { id: 'gift-10', name: '纪念品', description: '珍贵的回忆', price: 300, rarity: 'legendary', effect: { favorabilityBonus: 30, unlockDiary: true }, imageUrl: '' }
      ]

      for (const gift of defaultGifts) {
        await new Promise((resolve, reject) => {
          const request = store.put(gift)
          request.onsuccess = () => resolve()
          request.onerror = () => reject(request.error)
        })
      }

      console.log('✅ 礼物目录已初始化')
    } catch (error) {
      console.error('❌ 初始化礼物目录失败:', error)
    }
  }

  // ==================== 聊天摘要管理 ====================

  /**
   * 保存聊天摘要
   */
  async saveChatSummary(summary) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['chatSummaries'], 'readwrite')
      const store = transaction.objectStore('chatSummaries')

      return new Promise((resolve, reject) => {
        const request = store.put(summary)
        request.onsuccess = () => {
          console.log('✅ 聊天摘要已保存:', summary.id)
          resolve(summary)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 保存聊天摘要失败:', error)
      throw error
    }
  }

  /**
   * 获取角色的所有摘要
   */
  async getCharacterSummaries(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['chatSummaries'], 'readonly')
      const store = transaction.objectStore('chatSummaries')
      const index = store.index('characterId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(characterId)
        request.onsuccess = () => {
          const summaries = (request.result || []).sort((a, b) => b.timestamp - a.timestamp)
          resolve(summaries)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取聊天摘要失败:', error)
      return []
    }
  }

  // ==================== 角色记忆管理 ====================

  /**
   * 保存角色记忆点
   */
  async saveCharacterMemory(memory) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterMemories'], 'readwrite')
      const store = transaction.objectStore('characterMemories')

      return new Promise((resolve, reject) => {
        const request = store.put(memory)
        request.onsuccess = () => {
          console.log('✅ 角色记忆已保存:', memory.id)
          resolve(memory)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 保存角色记忆失败:', error)
      throw error
    }
  }

  /**
   * 获取角色的所有记忆
   */
  async getCharacterMemories(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterMemories'], 'readonly')
      const store = transaction.objectStore('characterMemories')
      const index = store.index('characterId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(characterId)
        request.onsuccess = () => {
          const memories = (request.result || []).sort((a, b) => b.importance - a.importance)
          resolve(memories)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取角色记忆失败:', error)
      return []
    }
  }

  /**
   * 获取所有礼物
   */
  async getAllGifts() {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['giftCatalog'], 'readonly')
      const store = transaction.objectStore('giftCatalog')

      return new Promise((resolve, reject) => {
        const request = store.getAll()
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取礼物列表失败:', error)
      return []
    }
  }

  /**
   * 获取用户礼物库存
   */
  async getUserGifts(userId = 'guest') {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['userGifts'], 'readwrite')
      const store = transaction.objectStore('userGifts')

      return new Promise((resolve, reject) => {
        const request = store.get(userId)
        request.onsuccess = () => {
          const result = request.result
          if (!result) {
            // 初始化用户库存
            const defaultInventory = {
              userId,
              gifts: [],
              currency: 1000, // 初始货币
              updatedAt: new Date().toISOString()
            }
            // 在同一个事务中保存
            const putRequest = store.put(defaultInventory)
            putRequest.onsuccess = () => resolve(defaultInventory)
            putRequest.onerror = () => reject(putRequest.error)
          } else {
            resolve(result)
          }
        }
        request.onerror = () => reject(request.error)
        transaction.onerror = () => reject(transaction.error)
      })
    } catch (error) {
      console.error('❌ 获取用户礼物库存失败:', error)
      return { userId, gifts: [], currency: 1000 }
    }
  }

  /**
   * 购买礼物
   */
  async purchaseGift(userId, giftId) {
    try {
      const userGifts = await this.getUserGifts(userId)
      const allGifts = await this.getAllGifts()
      const gift = allGifts.find(g => g.id === giftId)

      if (!gift) {
        throw new Error('礼物不存在')
      }

      if (userGifts.currency < gift.price) {
        throw new Error('货币不足')
      }

      // 更新库存
      const existingGift = userGifts.gifts.find(g => g.giftId === giftId)
      if (existingGift) {
        existingGift.quantity += 1
      } else {
        userGifts.gifts.push({
          giftId,
          quantity: 1,
          obtainedAt: Date.now()
        })
      }

      userGifts.currency -= gift.price
      userGifts.updatedAt = new Date().toISOString()

      await this.ensureDB()
      const transaction = this.db.transaction(['userGifts'], 'readwrite')
      const store = transaction.objectStore('userGifts')

      return new Promise((resolve, reject) => {
        const request = store.put(userGifts)
        request.onsuccess = () => resolve(userGifts)
        request.onerror = () => reject(request.error)
        transaction.onerror = () => reject(transaction.error)
      })
    } catch (error) {
      console.error('❌ 购买礼物失败:', error)
      throw error
    }
  }

  /**
   * 赠送礼物给角色
   */
  async giveGiftToCharacter(userId, characterId, giftId) {
    try {
      const userGifts = await this.getUserGifts(userId)
      const gift = userGifts.gifts.find(g => g.giftId === giftId)

      if (!gift || gift.quantity <= 0) {
        throw new Error('礼物不足')
      }

      // 减少库存
      gift.quantity -= 1
      if (gift.quantity === 0) {
        userGifts.gifts = userGifts.gifts.filter(g => g.giftId !== giftId)
      }
      userGifts.updatedAt = new Date().toISOString()

      await this.ensureDB()
      const transaction = this.db.transaction(['userGifts'], 'readwrite')
      const store = transaction.objectStore('userGifts')
      await new Promise((resolve, reject) => {
        const request = store.put(userGifts)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
        transaction.onerror = () => reject(transaction.error)
      })

      // 返回礼物信息，让调用者根据角色反应决定好感度变化
      const allGifts = await this.getAllGifts()
      const giftInfo = allGifts.find(g => g.id === giftId)

      return { userGifts, giftInfo }
    } catch (error) {
      console.error('❌ 赠送礼物失败:', error)
      throw error
    }
  }

  // ==================== 点数管理系统 ====================

  /**
   * 获取角色点数
   * @param {string} characterId - 角色ID
   * @returns {Promise<number>} 当前点数
   */
  async getCharacterPoints(characterId) {
    try {
      const state = await this.getCharacterState(characterId)
      const points = state?.points !== undefined ? state.points : 1000
      console.log(`💰 角色 ${characterId} 当前点数:`, points)
      return points
    } catch (error) {
      console.error('❌ 获取角色点数失败:', error)
      return 1000 // 默认返回初始点数
    }
  }

  /**
   * 消耗点数
   * @param {string} characterId - 角色ID
   * @param {number} amount - 消耗数量
   * @param {string} reason - 消耗原因
   * @returns {Promise<{success: boolean, currentPoints: number, message: string}>}
   */
  async consumePoints(characterId, amount, reason = '换装生成') {
    try {
      const state = await this.getCharacterState(characterId)
      const currentPoints = state?.points !== undefined ? state.points : 1000
      
      // 检查点数是否足够
      if (currentPoints < amount) {
        console.warn(`⚠️ 点数不足: 需要 ${amount}，当前 ${currentPoints}`)
        return {
          success: false,
          currentPoints,
          message: `点数不足，需要 ${amount} 点，当前仅有 ${currentPoints} 点`
        }
      }

      // 扣除点数
      const newPoints = currentPoints - amount
      const pointsHistory = state?.pointsHistory || []
      
      // 添加消费记录
      pointsHistory.push({
        type: 'consume',
        amount,
        reason,
        timestamp: Date.now(),
        balanceBefore: currentPoints,
        balanceAfter: newPoints
      })

      // 保存更新后的状态
      await this.saveCharacterState(characterId, {
        ...state,
        points: newPoints,
        pointsHistory
      })

      console.log(`💸 消耗点数成功: -${amount} (${reason})，剩余 ${newPoints}`)
      return {
        success: true,
        currentPoints: newPoints,
        message: `成功消耗 ${amount} 点，剩余 ${newPoints} 点`
      }
    } catch (error) {
      console.error('❌ 消耗点数失败:', error)
      return {
        success: false,
        currentPoints: 0,
        message: '消耗点数失败：' + error.message
      }
    }
  }

  /**
   * 增加点数
   * @param {string} characterId - 角色ID
   * @param {number} amount - 增加数量
   * @param {string} reason - 增加原因
   * @returns {Promise<{success: boolean, currentPoints: number, message: string}>}
   */
  async addPoints(characterId, amount, reason = '系统奖励') {
    try {
      const state = await this.getCharacterState(characterId)
      const currentPoints = state?.points !== undefined ? state.points : 1000
      const newPoints = currentPoints + amount
      const pointsHistory = state?.pointsHistory || []
      
      // 添加收入记录
      pointsHistory.push({
        type: 'earn',
        amount,
        reason,
        timestamp: Date.now(),
        balanceBefore: currentPoints,
        balanceAfter: newPoints
      })

      // 保存更新后的状态
      await this.saveCharacterState(characterId, {
        ...state,
        points: newPoints,
        pointsHistory
      })

      console.log(`💰 增加点数成功: +${amount} (${reason})，当前 ${newPoints}`)
      return {
        success: true,
        currentPoints: newPoints,
        message: `成功获得 ${amount} 点，当前 ${newPoints} 点`
      }
    } catch (error) {
      console.error('❌ 增加点数失败:', error)
      return {
        success: false,
        currentPoints: 0,
        message: '增加点数失败：' + error.message
      }
    }
  }

  /**
   * 获取点数历史记录
   * @param {string} characterId - 角色ID
   * @param {number} limit - 限制返回数量（可选）
   * @returns {Promise<Array>} 点数历史记录
   */
  async getPointsHistory(characterId, limit = 50) {
    try {
      const state = await this.getCharacterState(characterId)
      const history = state?.pointsHistory || []
      
      // 按时间倒序排序，返回最近的记录
      const sortedHistory = [...history].sort((a, b) => b.timestamp - a.timestamp)
      const limitedHistory = limit ? sortedHistory.slice(0, limit) : sortedHistory
      
      console.log(`📜 获取点数历史记录: ${limitedHistory.length} 条`)
      return limitedHistory
    } catch (error) {
      console.error('❌ 获取点数历史失败:', error)
      return []
    }
  }

  /**
   * 重置角色点数（管理员功能）
   * @param {string} characterId - 角色ID
   * @param {number} points - 新的点数值
   * @returns {Promise<boolean>}
   */
  async resetCharacterPoints(characterId, points = 1000) {
    try {
      const state = await this.getCharacterState(characterId)
      const pointsHistory = state?.pointsHistory || []
      
      // 添加重置记录
      pointsHistory.push({
        type: 'reset',
        amount: points,
        reason: '管理员重置',
        timestamp: Date.now(),
        balanceBefore: state?.points || 0,
        balanceAfter: points
      })

      await this.saveCharacterState(characterId, {
        ...state,
        points,
        pointsHistory
      })

      console.log(`🔄 重置点数成功: ${points}`)
      return true
    } catch (error) {
      console.error('❌ 重置点数失败:', error)
      return false
    }
  }
}

// 导出单例
const playCreateStorageService = new PlayCreateStorageService()

// 初始化礼物目录
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(() => {
      playCreateStorageService.initializeGiftCatalog()
    }, 1000)
  })
}

export default playCreateStorageService

